import { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { useLang } from "../../lib/LangContext";

// H.264 High / yuv420p / faststart MP4s — plays in Chrome, Safari, Firefox, iOS and Android.
const SOURCES = { desktop: "/media/hero-1080.mp4", mobile: "/media/hero-720.mp4" };
// VP9 fallback for browsers built without H.264 (some Linux Chromium/Firefox builds).
const WEBM_FALLBACK = "/media/hero-720.webm";
export const HERO_POSTER = "/media/hero-poster-1920.jpg";
const POSTER_SET = "/media/hero-poster.jpg 1280w, /media/hero-poster-1920.jpg 1920w";

// Always play: Windows "Animation effects: off" sets prefers-reduced-motion, which previously hid the
// video on desktop Chrome. A visible pause control covers the accessibility need instead.
const pickSource = () => {
  if (typeof window === "undefined") return null;
  const small = window.matchMedia("(max-width: 767px)").matches || navigator.connection?.saveData;
  return small ? SOURCES.mobile : SOURCES.desktop;
};
const LABELS = { de: ["Video pausieren", "Video abspielen"], ua: ["Пауза відео", "Відтворити відео"], en: ["Pause video", "Play video"], pl: ["Wstrzymaj wideo", "Odtwórz wideo"], ru: ["Пауза видео", "Воспроизвести видео"] };

const tryPlay = (v) => {
  if (!v || !v.paused) return;
  v.muted = true;
  v.defaultMuted = true;
  const p = v.play();
  if (p && p.catch) p.catch(() => {});
};

// mode="loop" plays the brand film as a muted background loop.
export const HeroVideo = ({ mode = "loop", className = "" }) => {
  const ref = useRef(null);
  const [src, setSrc] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const pausedRef = useRef(false);
  const { lang } = useLang();
  const [pauseLabel, playLabel] = LABELS[lang] || LABELS.de;
  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    pausedRef.current = !pausedRef.current;
    setUserPaused(pausedRef.current);
    if (pausedRef.current) v.pause(); else tryPlay(v);
  };

  useEffect(() => { setSrc(pickSource()); }, []);

  useEffect(() => {
    const v = ref.current;
    if (!v || !src || mode !== "loop") return undefined;
    // iOS/Safari need the muted *attribute*, which React does not reliably render.
    v.setAttribute("muted", "");
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");
    v.muted = true;
    let visible = true;
    const play = () => visible && !document.hidden && !pausedRef.current && tryPlay(v);
    const io = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      if (visible) play(); else v.pause();
    }, { threshold: 0.01 });
    io.observe(v);
    // Retry on readiness, tab return and first user gesture (Low Power Mode / strict autoplay policies).
    const onVis = () => (document.hidden ? v.pause() : play());
    const gesture = () => play();
    v.addEventListener("loadeddata", play);
    v.addEventListener("canplay", play);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("touchstart", gesture, { passive: true, once: true });
    window.addEventListener("pointerdown", gesture, { once: true });
    window.addEventListener("scroll", gesture, { passive: true, once: true });
    play();
    return () => {
      io.disconnect();
      v.removeEventListener("loadeddata", play);
      v.removeEventListener("canplay", play);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("touchstart", gesture);
      window.removeEventListener("pointerdown", gesture);
      window.removeEventListener("scroll", gesture);
    };
  }, [src, mode]);

  return (
    <>
    <div className={`absolute inset-0 overflow-hidden ${className}`} aria-hidden="true">
      <img
        src={HERO_POSTER}
        srcSet={POSTER_SET}
        sizes="100vw"
        alt=""
        width="1920"
        height="1080"
        fetchPriority="high"
        decoding="async"
        data-testid="hero-poster"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {src && !failed && (
        <video
          ref={ref}
          data-testid="hero-video"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${playing ? "opacity-100" : "opacity-0"}`}
          poster={HERO_POSTER}
          muted
          loop={mode === "loop"}
          playsInline
          autoPlay={mode === "loop"}
          preload="auto"
          disablePictureInPicture
          disableRemotePlayback
          onPlaying={() => setPlaying(true)}
        >
          <source src={src} type={src.includes("1080") ? 'video/mp4; codecs="avc1.640028"' : 'video/mp4; codecs="avc1.64001F"'} />
          <source src={WEBM_FALLBACK} type='video/webm; codecs="vp9"' onError={() => setFailed(true)} />
        </video>
      )}
    </div>
    {src && !failed && mode === "loop" && (
      <button type="button" onClick={toggle} data-testid="hero-video-toggle" aria-label={userPaused ? playLabel : pauseLabel} aria-pressed={userPaused}
        className="absolute z-20 right-5 bottom-6 md:right-8 md:bottom-8 w-9 h-9 rounded-full inline-flex items-center justify-center bg-black/35 backdrop-blur border border-white/15 text-white/70 hover:text-white hover:border-white/40 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-gold">
        {userPaused ? <Play size={13} /> : <Pause size={13} />}
      </button>
    )}
    </>
  );
};
